import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Calendar,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  X,
  Link,
  ExternalLink,
  Clock,
  User,
} from 'lucide-react';
import { eventsApi } from '../../services/api';
import { Event, EventTypeConfig } from '../../types';
import { getEventIconComponent } from '../../utils/eventIcons';
import { Modal, Avatar } from '../../components/UI';
import { useAuthStore } from '../../store/authStore';
import styles from './Events.module.css';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDaysInMonth(y: number, m: number) { return new Date(y, m + 1, 0).getDate(); }
function getFirstDayOfMonth(y: number, m: number) { return new Date(y, m, 1).getDay(); }
function isSameDay(y: number, m: number, d: number) {
  const t = new Date();
  return t.getFullYear() === y && t.getMonth() === m && t.getDate() === d;
}

const Events: React.FC = () => {
  const { user } = useAuthStore();
  const canCreate = user?.role === 'superadmin' || user?.role === 'admin';
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [filters, setFilters] = useState<Set<string>>(new Set());
  const [highlightedDay, setHighlightedDay] = useState<number | null>(null);
  const calendarGridRef = useRef<HTMLDivElement>(null);
  const [eventTypes, setEventTypes] = useState<EventTypeConfig[]>([]);
  const [eventReactions, setEventReactions] = useState<Record<string, { count: number; userReacted: boolean }>>({});
  const [showReactionModal, setShowReactionModal] = useState(false);
  const [reactionModalData, setReactionModalData] = useState<any[]>([]);
  const [reactionModalTitle, setReactionModalTitle] = useState('');

  // Cache fetched months to avoid re-fetching on back/forward navigation
  const monthCacheRef = useRef<Record<string, Event[]>>({});
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const eventTypeConfig = useMemo(() => {
    const config: Record<string, { label: string; color: string; icon_name: string }> = {};
    eventTypes.forEach(et => {
      config[et.key] = { label: et.name, color: et.color, icon_name: et.icon_name };
    });
    return config;
  }, [eventTypes]);

  // Calendar month index (0-based for JS Date)
  const calMonth = selectedMonth - 1;

  const fetchEvents = useCallback(async (year: number, month: number) => {
    const cacheKey = `${year}-${month}`;

    // Return cached data instantly if available
    if (monthCacheRef.current[cacheKey]) {
      setEvents(monthCacheRef.current[cacheKey]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await eventsApi.getByMonth(year, month);
      const eventData = response.data.events;
      monthCacheRef.current[cacheKey] = eventData;
      setEvents(eventData);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch event types on mount
  useEffect(() => {
    eventsApi.getEventTypes().then(res => {
      setEventTypes(res.data.eventTypes || []);
    }).catch(err => console.error('Failed to fetch event types:', err));
  }, []);

  // When eventTypes changes, update enabledTypes
  useEffect(() => {
    if (eventTypes.length > 0) {
      setFilters(new Set(eventTypes.map(et => et.key)));
    }
  }, [eventTypes]);

  // Debounced fetch on month/year change — prevents rapid-click API spam
  useEffect(() => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);

    // If cached, load immediately (no debounce needed)
    const cacheKey = `${selectedYear}-${selectedMonth}`;
    if (monthCacheRef.current[cacheKey]) {
      fetchEvents(selectedYear, selectedMonth);
      return;
    }

    // Otherwise debounce to handle rapid navigation
    fetchTimerRef.current = setTimeout(() => {
      fetchEvents(selectedYear, selectedMonth);
    }, 150);

    return () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    };
  }, [selectedMonth, selectedYear, fetchEvents]);

  const handlePrevMonth = () => {
    setSelectedDay(null);
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    setSelectedDay(null);
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Filter toggle — also zoom to first matching date card
  const toggleFilter = (type: string) => {
    // Ensure the type is visible in filters
    if (!filters.has(type)) {
      const next = new Set(filters);
      next.add(type);
      setFilters(next);
    }

    // Zoom to the first event of this type
    const firstMatch = events
      .filter(e => (e.event_type || 'custom') === type)
      .sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime())[0];
    if (firstMatch) {
      const day = new Date(firstMatch.event_date).getDate();
      setSelectedDay(day);
      setSelectedEvent(firstMatch);
      setHighlightedDay(null); // Reset to retrigger animation
      setTimeout(() => {
        setHighlightedDay(day);
        const cell = calendarGridRef.current?.querySelector(`[data-day="${day}"]`) as HTMLElement;
        if (cell) {
          cell.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => setHighlightedDay(null), 1500);
      }, 50);
    }
  };

  // Type counts for filter chips
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    eventTypes.forEach(et => { counts[et.key] = 0; });
    events.forEach(e => {
      const t = e.event_type || 'custom';
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [events, eventTypes]);

  // Filtered events
  const filteredEvents = useMemo(() => {
    let list = events.filter(e => filters.has(e.event_type || 'custom'));
    if (selectedDay) {
      list = list.filter(e => {
        const d = new Date(e.event_date);
        return d.getDate() === selectedDay;
      });
    }
    return list.sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime());
  }, [events, filters, selectedDay]);

  // Events for a specific calendar day
  const dayEvents = (day: number) => {
    return events.filter(e => {
      const d = new Date(e.event_date);
      return d.getDate() === day && filters.has(e.event_type || 'custom');
    });
  };

  // Upcoming events (next 4 from now, filtering out today's past events by time in IST)
  const upcomingEvents = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:00`;

    return [...events]
      .filter(e => {
        const eventDate = new Date(e.event_date);
        eventDate.setHours(0, 0, 0, 0);
        // Future dates: always include
        if (eventDate > today) return true;
        // Past dates: exclude
        if (eventDate < today) return false;
        // Today: check if event has already ended
        if (e.end_time && e.end_time < currentTime) return false;
        if (!e.end_time && e.start_time && e.start_time < currentTime) return false;
        return true;
      })
      .sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime())
      .slice(0, 4);
  }, [events]);

  // Synthetic event IDs encode (eventType, targetUserId, year[, month]) — see eventController.ts
  // Format: `birthday-<userId>-<year>(-<month>)?` or `anniversary-<userId>-<year>(-<month>)?`
  // The userId itself is a UUID (contains dashes), so we slice off the type prefix and the trailing year/month.
  const parseSyntheticEventId = (eventId: string): { eventType: 'birthday' | 'work_anniversary'; targetUserId: string; year: number } | null => {
    let prefix: 'birthday-' | 'anniversary-' | null = null;
    let eventType: 'birthday' | 'work_anniversary' | null = null;
    if (eventId.startsWith('birthday-')) { prefix = 'birthday-'; eventType = 'birthday'; }
    else if (eventId.startsWith('anniversary-')) { prefix = 'anniversary-'; eventType = 'work_anniversary'; }
    if (!prefix || !eventType) return null;

    const rest = eventId.slice(prefix.length);
    // The user UUID has 4 dashes (8-4-4-4-12). The trailing parts are year (-month?). Strip from the right.
    const parts = rest.split('-');
    if (parts.length < 6) return null; // 5 UUID parts + year minimum
    // Year is the 6th-from-end if month is present (parts.length >= 7), or last otherwise.
    const yearIdx = parts.length === 6 ? 5 : parts.length - 2;
    const year = parseInt(parts[yearIdx], 10);
    if (isNaN(year)) return null;
    const targetUserId = parts.slice(0, 5).join('-');
    return { eventType, targetUserId, year };
  };

  // Fetch reactions/wishes for upcoming events (real events use reactions; synthetic events use wishes)
  useEffect(() => {
    if (upcomingEvents.length === 0) return;
    const fetches = upcomingEvents.map((e: any) => {
      const syn = parseSyntheticEventId(String(e.id));
      if (syn) {
        return eventsApi.getEventWishes(syn.targetUserId, syn.eventType, syn.year).catch(() => null);
      }
      return eventsApi.getEventReactions(e.id).catch(() => null);
    });
    Promise.all(fetches).then(results => {
      const reactionsMap: Record<string, { count: number; userReacted: boolean }> = {};
      upcomingEvents.forEach((e: any, i: number) => {
        const r = results[i]?.data;
        if (r) {
          reactionsMap[e.id] = { count: r.count, userReacted: r.user_reacted };
        }
      });
      setEventReactions(prev => ({ ...prev, ...reactionsMap }));
    });
  }, [upcomingEvents]);

  const handleViewReactions = async (eventId: string, eventTitle: string) => {
    try {
      const syn = parseSyntheticEventId(eventId);
      const res = syn
        ? await eventsApi.getEventWishDetails(syn.targetUserId, syn.eventType, syn.year)
        : await eventsApi.getEventReactionDetails(eventId);
      // Wishes endpoint returns rows without `reaction_type`. Stamp a synthetic label so the modal
      // can render a consistent right-side tag ("Wished" / "Celebrated").
      const data = syn
        ? res.data.map((r: any) => ({ ...r, reaction_type: syn.eventType === 'birthday' ? 'wished' : 'celebrated' }))
        : res.data;
      setReactionModalData(data);
      setReactionModalTitle(eventTitle);
      setShowReactionModal(true);
    } catch (err) { console.error(err); }
  };

  // Calendar cells
  const calendarCells = useMemo(() => {
    const dim = getDaysInMonth(selectedYear, calMonth);
    const first = getFirstDayOfMonth(selectedYear, calMonth);
    const cells: (number | null)[] = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= dim; d++) cells.push(d);
    return cells;
  }, [selectedYear, calMonth]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const getEventIcon = (event: any) => {
    const iconName = event.icon_name || eventTypeConfig[event.event_type]?.icon_name || 'party';
    const IconComponent = getEventIconComponent(iconName);
    return <IconComponent size={18} />;
  };

  const getEventIconSmall = (event: any) => {
    const iconName = event.icon_name || eventTypeConfig[event.event_type]?.icon_name || 'party';
    const IconComponent = getEventIconComponent(iconName);
    return <IconComponent size={10} />;
  };

  const getTypeName = (type: string) => eventTypeConfig[type]?.label || 'Event';

  const getDaysUntil = (dateString: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dateString);
    d.setHours(0, 0, 0, 0);
    return Math.ceil((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  // -- CRUD handlers (unchanged) --

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1>Events</h1>
          <p>{events.length} in {monthNames[selectedMonth - 1]} — {events.filter(e => e.event_type === 'birthday').length} birthdays, {events.filter(e => e.event_type === 'work_anniversary').length} anniversaries, {events.filter(e => e.event_type !== 'birthday' && e.event_type !== 'work_anniversary').length} sessions.</p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewToggleBtn} ${view === 'calendar' ? styles.active : ''}`}
              onClick={() => setView('calendar')}
            >
              Month
            </button>
            <button
              className={`${styles.viewToggleBtn} ${view === 'list' ? styles.active : ''}`}
              onClick={() => setView('list')}
            >
              Agenda
            </button>
          </div>
        </div>
      </div>

      {/* Coming Up - next 4 upcoming events */}
      {upcomingEvents.length > 0 && (
        <div className={styles.comingUpHero}>
          <div className={styles.comingUpHeroHeader}>
            <div className={styles.calIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            </div>
            <span>Coming Up</span>
          </div>
          <div className={styles.comingUpHeroCards}>
            {upcomingEvents.map(ev => {
              const type = ev.event_type || 'custom';
              const typeColor = eventTypeConfig[type]?.color || '#6366F1';
              const typeLabel = eventTypeConfig[type]?.label || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              const diff = getDaysUntil(ev.event_date);
              const actionLabel = type === 'birthday' ? 'Wish' : type === 'work_anniversary' ? 'Celebrate' : 'RSVP';
              const reaction = eventReactions[ev.id];
              const reactionCount = reaction?.count || 0;
              const userReacted = reaction?.userReacted || false;
              return (
                <div
                  key={ev.id}
                  className={styles.comingUpHeroCard}
                  onClick={() => setSelectedEvent(ev)}
                >
                  <div className={styles.comingUpCardHeader}>
                    <div className={styles.comingUpTypeRow}>
                      <span className={styles.comingUpTypeDot} style={{ background: typeColor }} />
                      <span className={styles.comingUpTypeLabel}>{typeLabel}</span>
                    </div>
                    <div className={styles.comingUpIconBadge} style={{ background: `${typeColor}15`, color: typeColor }}>
                      {(() => { const IC = getEventIconComponent(eventTypeConfig[type]?.icon_name || 'calendar'); return <IC size={16} />; })()}
                    </div>
                  </div>
                  <div className={styles.comingUpHeroCardTitle}>{ev.title}</div>
                  <div className={styles.comingUpHeroCardDate}>{formatDate(ev.event_date)}</div>
                  <div className={styles.comingUpCardFooter}>
                    <span className={styles.comingUpCountdown}>
                      {diff <= 0 ? 'Today' : diff === 1 ? 'Tomorrow' : `In ${diff} days`}
                    </span>
                    <button
                      className={`${styles.comingUpActionBtn} ${userReacted ? styles.comingUpActionBtnActive : ''}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const syn = parseSyntheticEventId(String(ev.id));
                          const res = syn
                            ? await eventsApi.toggleEventWish(syn.targetUserId, syn.eventType, syn.year)
                            : await eventsApi.reactToEvent(ev.id, type === 'work_anniversary' ? 'celebrate' : 'rsvp');
                          setEventReactions(prev => ({
                            ...prev,
                            [ev.id]: {
                              count: res.data.count,
                              userReacted: res.data.action !== 'removed',
                            },
                          }));
                        } catch (err) {
                          console.error('Reaction failed', err);
                        }
                      }}
                    >
                      {actionLabel}
                    </button>
                    {reactionCount > 0 && (
                      <button
                        className={styles.comingUpCountLabel}
                        onClick={(e) => { e.stopPropagation(); handleViewReactions(ev.id, ev.title); }}
                      >
                        {reactionCount}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <span>Loading events...</span>
        </div>
      ) : (
        <div className={styles.mainGrid}>
          {/* Left: Calendar or List */}
          <div>
            {/* Month Navigation */}
            <div className={styles.monthNav}>
              <div className={styles.monthNavLeft}>
                <button className={styles.navBtn} onClick={handlePrevMonth}>
                  <ChevronLeft size={15} />
                </button>
                <h2 className={styles.monthTitle}>
                  {monthNames[selectedMonth - 1]} {selectedYear}
                </h2>
                <button className={styles.navBtn} onClick={handleNextMonth}>
                  <ChevronRight size={15} />
                </button>
              </div>
              <div className={styles.monthNavRight}>
                {selectedDay && (
                  <button className={styles.clearFilterBtn} onClick={() => setSelectedDay(null)}>
                    Clear filter
                  </button>
                )}
                <span className={styles.eventCount}>{events.length} events</span>
              </div>
            </div>

            {view === 'calendar' ? (
              <div className={styles.calendarCard}>
                <div className={styles.calendarDayHeaders}>
                  {dayNamesShort.map(d => (
                    <div key={d} className={styles.calendarDayHeader}>{d}</div>
                  ))}
                </div>
                <div className={styles.calendarGrid} ref={calendarGridRef}>
                  {calendarCells.map((day, i) => {
                    if (day === null) {
                      return <div key={`empty-${i}`} className={styles.calendarCellEmpty} />;
                    }
                    const de = dayEvents(day);
                    const isToday = isSameDay(selectedYear, calMonth, day);
                    const isSelected = selectedDay === day;
                    const isHighlighted = highlightedDay === day;
                    return (
                      <div
                        key={day}
                        data-day={day}
                        className={`${styles.calendarCell} ${isSelected ? styles.selected : ''} ${isToday ? styles.today : ''} ${isHighlighted ? styles.highlighted : ''}`}
                        onClick={() => setSelectedDay(isSelected ? null : day)}
                      >
                        <div style={{ marginBottom: 4 }}>
                          <span className={`${styles.dayNumber} ${isToday ? styles.today : ''}`}>{day}</span>
                        </div>
                        <div className={styles.cellEvents}>
                          {de.slice(0, 2).map(ev => (
                            <div
                              key={ev.id}
                              className={styles.cellEventDot}
                              style={{ background: `${eventTypeConfig[ev.event_type || 'custom']?.color || '#6366F1'}15`, color: eventTypeConfig[ev.event_type || 'custom']?.color || '#6366F1' }}
                              onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
                            >
                              {getEventIconSmall(ev)}
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {ev.title.length > 15 ? ev.title.slice(0, 15) + '…' : ev.title}
                              </span>
                            </div>
                          ))}
                          {de.length > 2 && (
                            <span className={styles.cellEventMore}>+{de.length - 2} more</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className={styles.listView}>
                {filteredEvents.length === 0 ? (
                  <div className={styles.empty}>
                    <Calendar size={38} />
                    <h3>No events match your filters</h3>
                  </div>
                ) : (
                  filteredEvents.map(ev => {
                    const type = ev.event_type || 'custom';
                    const typeColor = eventTypeConfig[type]?.color || '#6366F1';
                    return (
                      <div
                        key={ev.id}
                        className={styles.listCard}
                        style={{ borderLeftColor: typeColor }}
                        onClick={() => setSelectedEvent(ev)}
                      >
                        <div className={styles.listCardIcon} style={{ background: `${typeColor}15`, color: typeColor }}>
                          {getEventIcon(ev)}
                        </div>
                        <div className={styles.listCardContent}>
                          <div className={styles.listCardMeta}>
                            <span className={styles.listCardBadge} style={{ background: `${typeColor}15`, color: typeColor }}>
                              {getTypeName(type)}
                            </span>
                          </div>
                          <div className={styles.listCardTitle}>{ev.title}</div>
                          {ev.user_first_name && (
                            <div className={styles.listCardPerson}>
                              {ev.user_first_name} {ev.user_last_name}
                            </div>
                          )}
                        </div>
                        <div className={styles.listCardDate}>
                          {formatDate(ev.event_date)}
                          {ev.start_time && (
                            <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>
                              {new Date(`2000-01-01T${ev.start_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                              {ev.end_time && ` – ${new Date(`2000-01-01T${ev.end_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className={styles.sidebar}>
            {/* Event Detail Panel */}
            {selectedEvent && (
              <div className={styles.sidebarCard}>
                <div className={styles.detailStrip} style={{ background: eventTypeConfig[selectedEvent.event_type || 'custom']?.color || '#6366F1' }} />
                <div className={styles.detailBody}>
                  <div className={styles.detailTop}>
                    <div className={styles.detailIconBox} style={{ background: `${eventTypeConfig[selectedEvent.event_type || 'custom']?.color || '#6366F1'}15`, color: eventTypeConfig[selectedEvent.event_type || 'custom']?.color || '#6366F1' }}>
                      {React.cloneElement(getEventIcon(selectedEvent) as React.ReactElement, { size: 28 })}
                    </div>
                    <button className={styles.detailCloseBtn} onClick={() => setSelectedEvent(null)}>
                      <X size={13} />
                    </button>
                  </div>
                  <span className={styles.detailType} style={{ color: eventTypeConfig[selectedEvent.event_type || 'custom']?.color || '#6366F1', background: `${eventTypeConfig[selectedEvent.event_type || 'custom']?.color || '#6366F1'}15` }}>
                    {getTypeName(selectedEvent.event_type || 'custom')}
                  </span>
                  <h3 className={styles.detailTitle}>{selectedEvent.title}</h3>
                  <div className={styles.detailInfo}>
                    <div className={styles.detailInfoRow}>
                      <Calendar size={14} style={{ color: '#94A3B8' }} />
                      <span>{formatDate(selectedEvent.event_date)}</span>
                    </div>
                    {selectedEvent.start_time && (
                      <div className={styles.detailInfoRow}>
                        <Clock size={14} style={{ color: '#94A3B8' }} />
                        <span>
                          {new Date(`2000-01-01T${selectedEvent.start_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                          {selectedEvent.end_time && ` – ${new Date(`2000-01-01T${selectedEvent.end_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`}
                          {selectedEvent.start_time && selectedEvent.end_time && (() => {
                            const [sh, sm] = selectedEvent.start_time!.split(':').map(Number);
                            const [eh, em] = selectedEvent.end_time!.split(':').map(Number);
                            const diffMin = (eh * 60 + em) - (sh * 60 + sm);
                            if (diffMin > 0) {
                              const hours = Math.floor(diffMin / 60);
                              const mins = diffMin % 60;
                              return <span style={{ color: '#94A3B8', marginLeft: '6px' }}>({hours > 0 ? `${hours}h` : ''}{mins > 0 ? ` ${mins}m` : ''})</span>;
                            }
                            return null;
                          })()}
                        </span>
                      </div>
                    )}
                    {selectedEvent.user_first_name && (
                      <div className={styles.detailInfoRow}>
                        <User size={14} style={{ color: '#94A3B8' }} />
                        <span>{selectedEvent.user_first_name} {selectedEvent.user_last_name}</span>
                      </div>
                    )}
                  </div>
                  {selectedEvent.image_url && (
                    <div className={styles.detailImage}>
                      <img src={selectedEvent.image_url} alt={selectedEvent.title} />
                    </div>
                  )}
                  {selectedEvent.description && (
                    <p className={styles.detailDescription}>{selectedEvent.description}</p>
                  )}
                  {selectedEvent.link_url && (
                    <a href={selectedEvent.link_url} target="_blank" rel="noopener noreferrer" className={styles.detailLink}>
                      <Link size={14} />
                      {selectedEvent.link_title || selectedEvent.link_url}
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Legend - only show event types with events this month */}
            {eventTypes.some(et => typeCounts[et.key] > 0) && (
            <div className={styles.sidebarCard} style={{ padding: 20 }}>
              <h4 className={styles.legendTitle}>Event Types</h4>
              <div className={styles.legendList}>
                {eventTypes.filter(et => typeCounts[et.key] > 0).map(et => {
                  const IconComponent = getEventIconComponent(et.icon_name);
                  return (
                    <div key={et.key} className={styles.legendItem}>
                      <div className={styles.legendItemIcon} style={{ background: `${et.color}15` }}>
                        <IconComponent size={15} style={{ color: et.color }} />
                      </div>
                      <span className={styles.legendItemLabel}>{et.name}</span>
                      <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#64748B', fontWeight: 500 }}>{typeCounts[et.key]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Event Responses Modal */}
      {showReactionModal && (
        <Modal isOpen={showReactionModal} onClose={() => setShowReactionModal(false)} title="Event Responses">
          <div style={{ minWidth: 300 }}>
            <p style={{ fontSize: 13, color: '#8AA89C', marginBottom: 12, borderBottom: '1px solid #E2EDE9', paddingBottom: 8 }}>
              {reactionModalData.length} response{reactionModalData.length !== 1 ? 's' : ''} for "{reactionModalTitle}"
            </p>
            {reactionModalData.map((r: any, i: number) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                <Avatar src={r.avatar_url} name={`${r.first_name} ${r.last_name}`} size="sm" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1A2E27' }}>{r.first_name} {r.last_name}</div>
                </div>
                <span style={{ fontSize: 11, color: '#8AA89C', textTransform: 'capitalize' as const }}>{r.reaction_type}</span>
              </div>
            ))}
          </div>
        </Modal>
      )}

    </div>
  );
};

export default Events;
